/******************************************************************************
 *** MbrasChangeHistory ** 변경 이력 조회
 *******************************************************************************/
import AppContext from "@/js/common/AppContext";
import Axios from "@/js/common/Axios";
import Util from "@/js/common/Util";
import React from "react";
import MbrasDepartHistory from "./MbrasDepartHistory";
import MbrasLeaveHistory from "./MbrasLeaveHistory";
import MbrasNewHistory from "./MbrasNewHistory";
import MbrasRegulHistory from "./MbrasRegulHistory";
import MbrasRejoinHistory from "./MbrasRejoinHistory";
import MbrasResignHistory from "./MbrasResignHistory";
import MbrasRetireHistory from "./MbrasRetireHistory";
import MbrasReturnHistory from "./MbrasReturnHistory";
import MbrasWhdwlHistory from "./MbrasWhdwlHistory";
import Button from "@/js/common/ui/Button";
import MbrasLeaveExtendHistory from "./MbrasLeaveExtendHistory";

interface Props {
    mbrasId     : string;
    setModalOpen: Function;
}

interface ChangeHistoryRow {
    mbrasAtrzDocId : string;
    mbrasId        : string;
    mbrasAtrzSeCd  : string;
    applyDt        : string;
    atrzReqDt      : string;
    atrzDt         : string;
    regMm          : string;
}

const MbrasChangeHistory = ({ mbrasId, setModalOpen }: Props) => {
    const [mbrasNm           , setMbrasNm           ] = React.useState("");                  // 성명
    const [telno             , setTelno             ] = React.useState("");                  // 휴대전화번호
    const [brdtDate          , setBrdtDate          ] = React.useState("");                  // 생년월일
    const [gndrCd            , setGndrCd            ] = React.useState("");                  // 성별
    const [upOgnzNm          , setUpOgnzNm          ] = React.useState("");                  // 본부명
    const [ognzNm            , setOgnzNm            ] = React.useState("");                  // 지부명
    const [wtjrNm            , setWtjrNm            ] = React.useState("");                  // 관내
    const [mbrasJoinSttsCd   , setMbrasJoinSttsCd   ] = React.useState("");                  // 조합가입상태
    const [historyData       , setHistoryData       ] = React.useState<ChangeHistoryRow[]>([]); // 변경 이력
    const [selectedHistory   , setSelectedHistory   ] = React.useState<ChangeHistoryRow>();    // 선택한 변경 이력
    const [selectedHistoryIdx, setSelectedHistoryIdx] = React.useState<number>(-1);             // 선택한 변경 이력 순번

    React.useEffect(() => {
        setMbrasNm("");
        setTelno("");
        setBrdtDate("");
        setGndrCd("");
        setUpOgnzNm("");
        setOgnzNm("");
        setWtjrNm("");
        setMbrasJoinSttsCd("");
        setHistoryData([]);
        setSelectedHistory(undefined);
        setSelectedHistoryIdx(-1);

        if (Util.isEmpty(mbrasId)) return;

        AppContext.showSpinner();

        Axios.dataAccess({
            url       : "system/mbras/chg/select-mbras-chg-detail.do",
            methodType: "post",
            paramType : "json",
            paramData : {
                mbrasId: mbrasId,
            },
            onSuccessFn: (res: any) => {
                const item = res.result.item;

                setMbrasNm(item.mbrasNm);
                setTelno(item.telno);
                setBrdtDate(item.brdtDate);
                setGndrCd(item.gndrCd);
                setUpOgnzNm(item.upOgnzNm);
                setOgnzNm(item.ognzNm);
                setWtjrNm(item.wtjrNm);
                setMbrasJoinSttsCd(item.mbrasJoinSttsCd);

                Axios.dataAccess({
                    url       : "system/mbras/mng/select-mbras-atrz-chg-history.do",
                    methodType: "post",
                    paramType : "json",
                    paramData : {
                        mbrasId: mbrasId,
                    },
                    onSuccessFn: (historyRes: any) => {
                        const list = Array.isArray(historyRes.result.list) ? historyRes.result.list : [];

                        setHistoryData(list);

                        AppContext.hideSpinner();
                    },
                });
            },
        });
    }, [mbrasId]);

    const isWithdraw = mbrasJoinSttsCd === "03" || mbrasJoinSttsCd === "06" || mbrasJoinSttsCd === "07";
    const selectHistory = (item: ChangeHistoryRow, index: number) => {
        setSelectedHistory(item);
        setSelectedHistoryIdx(index);
    };

    const renderHistoryDetail = () => {
        if (!selectedHistory) return null;

        const detailProps = {
            mbrasId        : mbrasId,
            mbrasAtrzDocId : selectedHistory.mbrasAtrzDocId,
            historyData    : selectedHistory,
        };

        switch (selectedHistory.mbrasAtrzSeCd) {
            case "01":
                return <MbrasNewHistory {...detailProps} />;

            case "03":
                return <MbrasWhdwlHistory {...detailProps} />;

            case "04":
                return <MbrasRegulHistory {...detailProps} />;

            case "05":
                return <MbrasDepartHistory {...detailProps} />;

            case "06":
                return <MbrasLeaveHistory {...detailProps} />;

            case "07":
                return <MbrasLeaveExtendHistory {...detailProps} />;

            case "08":
                return <MbrasReturnHistory {...detailProps} />;

            case "09":
                return <MbrasResignHistory {...detailProps} />;

            case "10":
                return <MbrasRetireHistory {...detailProps} />;

            case "11":
                return <MbrasRejoinHistory {...detailProps} />;

            default:
                return null;
        }
    };

    const closeModal = () => setModalOpen(false);

    return (
        <div
            className="w100p bgcWhite p10"
            style={{
                boxSizing    : "border-box",
                minHeight    : "100%",
                display      : "flex",
                flexDirection: "column",
            }}
        >
            <div style={{ flex: 1 }}>
            {/* 조합원 기본정보 */}
            <div className="mt5">
                <div className="dpFlx aiC gap8 mb10 fm-b fs15 fc111111 ls06">
                    <span className="w4 h16 bdrd2 bgc007bff" />
                    조합원 정보
                </div>

                <div
                    className="bd1 bdcDDDDDD bdrd10 bgcWhite"
                    style={{ boxShadow: "0 5px 16px rgba(31, 41, 55, 0.04)", overflow: "hidden" }}
                >
                    {isWithdraw ? (
                        <React.Fragment>
                            {/* 1행 */}
                            <div className="dpFlx bdb1 bdcDDDDDD">
                                <div className="dpFlx flx1">
                                    <div className="w120 h46 dpFlx aiC jcC bsBdb pl12 pr12 bgcFAFAFA fm-b fs13 fc4B5563 ls06 bdr1 bdcDDDDDD">
                                        성명
                                    </div>
                                    <div className="flx1 h46 dpFlx aiC bsBdb pl18 pr18 bgcWhite fm-c fs14 fc222222 ls06">
                                        {Util.isEmpty(mbrasNm) ? "-" : mbrasNm}
                                    </div>
                                </div>

                                <div className="dpFlx flx1 bdl1 bdcDDDDDD">
                                    <div className="w120 h46 dpFlx aiC jcC bsBdb pl12 pr12 bgcFAFAFA fm-b fs13 fc4B5563 ls06 bdr1 bdcDDDDDD">
                                        본부
                                    </div>
                                    <div className="flx1 h46 dpFlx aiC bsBdb pl18 pr18 bgcWhite fm-c fs14 fc222222 ls06">
                                        {Util.isEmpty(upOgnzNm) ? "-" : upOgnzNm}
                                    </div>
                                </div>
                            </div>

                            {/* 2행 */}
                            <div className="dpFlx">
                                <div className="dpFlx flx1">
                                    <div className="w120 h46 dpFlx aiC jcC bsBdb pl12 pr12 bgcFAFAFA fm-b fs13 fc4B5563 ls06 bdr1 bdcDDDDDD">
                                        지부
                                    </div>
                                    <div className="flx1 h46 dpFlx aiC bsBdb pl18 pr18 bgcWhite fm-c fs14 fc222222 ls06">
                                        {Util.isEmpty(ognzNm) ? "-" : ognzNm}
                                    </div>
                                </div>

                                <div className="dpFlx flx1 bdl1 bdcDDDDDD">
                                    <div className="w120 h46 dpFlx aiC jcC bsBdb pl12 pr12 bgcFAFAFA fm-b fs13 fc4B5563 ls06 bdr1 bdcDDDDDD">
                                        관내
                                    </div>
                                    <div className="flx1 h46 dpFlx aiC bsBdb pl18 pr18 bgcWhite fm-c fs14 fc222222 ls06">
                                        {Util.isEmpty(wtjrNm) ? "-" : wtjrNm}
                                    </div>
                                </div>
                            </div>
                        </React.Fragment>
                    ) : (
                        <React.Fragment>
                            {/* 1행 */}
                            <div className="dpFlx bdb1 bdcDDDDDD">
                                <div className="dpFlx flx1">
                                    <div className="w120 h46 dpFlx aiC jcC bsBdb pl12 pr12 bgcFAFAFA fm-b fs13 fc4B5563 ls06 bdr1 bdcDDDDDD">
                                        성명
                                    </div>
                                    <div className="flx1 h46 dpFlx aiC bsBdb pl18 pr18 bgcWhite fm-c fs14 fc222222 ls06">
                                        {Util.isEmpty(mbrasNm) ? "-" : mbrasNm}
                                    </div>
                                </div>

                                <div className="dpFlx flx1 bdl1 bdcDDDDDD">
                                    <div className="w120 h46 dpFlx aiC jcC bsBdb pl12 pr12 bgcFAFAFA fm-b fs13 fc4B5563 ls06 bdr1 bdcDDDDDD">
                                        휴대전화번호
                                    </div>
                                    <div className="flx1 h46 dpFlx aiC bsBdb pl18 pr18 bgcWhite fm-c fs14 fc222222 ls06">
                                        {Util.isEmpty(telno) ? "-" : Util.setPhoneNum(telno)}
                                    </div>
                                </div>
                            </div>

                            {/* 2행 */}
                            <div className="dpFlx">
                                <div className="dpFlx flx1">
                                    <div className="w120 h46 dpFlx aiC jcC bsBdb pl12 pr12 bgcFAFAFA fm-b fs13 fc4B5563 ls06 bdr1 bdcDDDDDD">
                                        생년월일
                                    </div>
                                    <div className="flx1 h46 dpFlx aiC bsBdb pl18 pr18 bgcWhite fm-c fs14 fc222222 ls06">
                                        {Util.isEmpty(brdtDate) ? "-" : Util.cvtDateStrForm(brdtDate, "-", "YYYYMMDD")}
                                    </div>
                                </div>

                                <div className="dpFlx flx1 bdl1 bdcDDDDDD">
                                    <div className="w120 h46 dpFlx aiC jcC bsBdb pl12 pr12 bgcFAFAFA fm-b fs13 fc4B5563 ls06 bdr1 bdcDDDDDD">
                                        성별
                                    </div>
                                    <div className="flx1 h46 dpFlx aiC bsBdb pl18 pr18 bgcWhite fm-c fs14 fc222222 ls06">
                                        {Util.isEmpty(gndrCd) ? "-" : Util.getValToCode("GNDR_CD", gndrCd)}
                                    </div>
                                </div>
                            </div>
                        </React.Fragment>
                    )}
                </div>
            </div>

            {/* 변경 이력 목록 */}
            <div className="mt20">
                <div className="dpFlx aiC gap8 mb10 fm-b fs15 fc111111 ls06">
                    <span className="w4 h16 bdrd2 bgc007bff" />
                    변경 이력
                </div>

                <div className="bd1 bdcCCCCCC bgcWhite">
                    <table style={{ width         : "100%", borderCollapse: "collapse", tableLayout   : "fixed", }}>
                        <colgroup>
                            <col style={{ width: "7%" }} />
                            <col style={{ width: "18%" }} />
                            <col style={{ width: "19%" }} />
                            <col style={{ width: "19%" }} />
                            <col style={{ width: "19%" }} />
                            <col style={{ width: "18%" }} />
                        </colgroup>

                        <thead>
                            <tr style={{height: 38, backgroundColor: "#F3F6F9", borderBottom: "1px solid #BFC8D2"}}>
                                <th className="fm-b fs13 fc333333 taC">번호</th>
                                <th className="fm-b fs13 fc333333 taC">구분</th>
                                <th className="fm-b fs13 fc333333 taC">신청일자</th>
                                <th className="fm-b fs13 fc333333 taC">상신일자</th>
                                <th className="fm-b fs13 fc333333 taC">승인일자</th>
                                <th className="fm-b fs13 fc333333 taC">등록월</th>
                            </tr>
                        </thead>

                        <tbody>
                            {historyData.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="fm-c fs13 fc777777 taC" style={{height: 80}}>
                                        변경 이력이 없습니다.
                                    </td>
                                </tr>
                            ) : (
                                historyData.map((item: ChangeHistoryRow, index: number) => {
                                    const isSelected = selectedHistoryIdx === index;

                                    return (
                                        <tr
                                            key={item.mbrasAtrzDocId}
                                            className="csP"
                                            style={{
                                                height         : 38,
                                                backgroundColor: isSelected ? "#EEF6FF" : "#FFFFFF",
                                                borderBottom   : "1px solid #D9D9D9",
                                                outline        : isSelected ? "1px solid #007BFF" : "none",
                                                outlineOffset  : -1,
                                            }}
                                            onClick={() => selectHistory(item, index)}
                                        >
                                            <td className="fm-c fs13 fc333333 taC">{index + 1}</td>
                                            <td className="fm-c fs13 fc333333 taC">{Util.getValToCode("MBRAS_ATRZ_SE_CD", item.mbrasAtrzSeCd)}</td>
                                            <td className="fm-c fs13 fc333333 taC">{Util.cvtDateStrForm(item.applyDt, "-", "YYYYMMDD")}</td>
                                            <td className="fm-c fs13 fc333333 taC">{Util.cvtDateStrForm(item.atrzReqDt, "-", "YYYYMMDD")}</td>
                                            <td className="fm-c fs13 fc333333 taC">{Util.cvtDateStrForm(item.atrzDt, "-", "YYYYMMDD")}</td>
                                            <td className="fm-c fs13 fc333333 taC">{Util.cvtMonthStrForm(item.regMm)}</td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* 선택 이력 상세 */}
            {selectedHistory && (
                <div className="mt25">
                    <div className="dpFlx aiC mb10">
                        <div className="fm-b fs16 fc007bff ls1 fw700 mr15">이력 상세내용</div>
                        <span
                            className="fm-b fs13 fc333333"
                            style={{
                                minWidth       : 90,
                                height         : 28,
                                padding        : "0 16px",
                                borderRadius   : 14,
                                backgroundColor: "#E5E5E5",
                                display        : "inline-flex",
                                alignItems     : "center",
                                justifyContent : "center",
                                boxSizing      : "border-box",
                            }}
                        >
                            {Util.getValToCode("MBRAS_ATRZ_SE_CD", selectedHistory.mbrasAtrzSeCd)}
                        </span>
                    </div>

                    <div
                        className="bd1 bdcDDDDDD bdrd10 bgcWhite"
                        style={{
                            padding: 14,
                        }}
                    >
                        {renderHistoryDetail()}
                    </div>
                </div>
            )}
            </div>

            {/* 버튼 */}
            <div
                className="dpFlx jcC mt25"
                style={{ paddingBottom: 20 }}
            >
                <Button
                    width={100}
                    height={40}
                    fontFamily="fm-b"
                    fontSize={16}
                    letterSpacing={-1}
                    variant="styCloseBtn"
                    borderRadius={6}
                    buttonClass="dpFlx aiC jcC"
                    buttonName="닫기"
                    onClickFunc={closeModal}
                />
            </div>
        </div>
    );
};

export default MbrasChangeHistory;